import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const emptyForm = { name: '', description: '', permissions: [] };

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchAll = () => {
    api.get('/roles').then((res) => setRoles(res.data)).catch(() => {});
    api.get('/roles/modules').then((res) => setModules(res.data)).catch(() => {});
  };

  useEffect(fetchAll, []);

  const getActions = (moduleKey) => form.permissions.find((p) => p.module === moduleKey)?.actions || [];

  const toggleAction = (moduleKey, action) => {
    setForm((f) => {
      const existing = f.permissions.find((p) => p.module === moduleKey);
      if (!existing) {
        return { ...f, permissions: [...f.permissions, { module: moduleKey, actions: [action] }] };
      }
      const hasAction = existing.actions.includes(action);
      const nextActions = hasAction ? existing.actions.filter((a) => a !== action) : [...existing.actions, action];
      return {
        ...f,
        permissions: f.permissions.map((p) =>
          p.module === moduleKey ? { ...p, actions: nextActions } : p
        ),
      };
    });
  };

  const toggleModuleAll = (moduleKey, allActions) => {
    const current = getActions(moduleKey);
    const allSelected = allActions.every((a) => current.includes(a));
    setForm((f) => ({
      ...f,
      permissions: allSelected
        ? f.permissions.filter((p) => p.module !== moduleKey)
        : [...f.permissions.filter((p) => p.module !== moduleKey), { module: moduleKey, actions: allActions }],
    }));
  };

  const startCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (role) => {
    setForm({ name: role.name, description: role.description, permissions: role.permissions });
    setEditingId(role._id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/roles/${editingId}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/roles', form);
        toast.success('Role created');
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save role');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await api.delete(`/roles/${id}`);
      toast.success('Role deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete role');
    }
  };

  return (
    <div className="space-y-6 text-[#2B1810] max-w-4xl">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Admin Roles & RBAC Permissions</h1>
          <p className="text-xs text-[#8A796C]">Configure granular module access for admin team members.</p>
        </div>

        <button onClick={startCreate} className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded shadow transition-colors">
          + Add New Role
        </button>
      </div>

      <div className="bg-white border border-[#E8DFD8] rounded-xl divide-y divide-[#E8DFD8] overflow-hidden shadow-sm">
        {roles.map((r) => (
          <div key={r._id} className="p-4 flex items-center justify-between hover:bg-[#FAF8F5]">
            <div>
              <p className="font-bold text-sm text-[#2B1810]">
                {r.name} {r.isSystem && <span className="text-[10px] font-bold bg-[#C49A45] text-white px-2 py-0.5 rounded uppercase tracking-wider ml-2">Super Admin System Role</span>}
              </p>
              <p className="text-xs text-[#8A796C]">{r.description}</p>
            </div>
            {!r.isSystem && (
              <div className="space-x-3 text-xs font-bold">
                <button onClick={() => startEdit(r)} className="text-[#C49A45] hover:underline">Edit</button>
                <button onClick={() => remove(r._id)} className="text-red-600 hover:underline">Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-6">
          <h3 className="font-heading text-base font-bold text-[#2B1810] border-b border-[#E8DFD8] pb-3">{editingId ? 'Edit Role' : 'Create New Role'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Role Name</label>
              <input required className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Description</label>
              <input className="input-field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <h4 className="font-bold text-xs uppercase tracking-wider text-[#2B1810]">Module Permissions</h4>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-2 border-t border-[#E8DFD8] pt-4">
            {modules.map((m) => {
              const current = getActions(m.key);
              return (
                <div key={m.key} className="border border-[#E8DFD8] rounded-lg p-4 bg-[#FAF8F5]">
                  <label className="flex items-center gap-2 font-bold text-xs text-[#2B1810] mb-2">
                    <input
                      type="checkbox"
                      className="accent-[#C49A45] w-4 h-4"
                      checked={m.actions.every((a) => current.includes(a)) && current.length > 0}
                      onChange={() => toggleModuleAll(m.key, m.actions)}
                    />
                    {m.label}
                  </label>
                  <div className="flex flex-wrap gap-3 pl-6">
                    {m.actions.map((action) => (
                      <label key={action} className="flex items-center gap-1.5 text-xs text-[#2B1810]">
                        <input
                          type="checkbox"
                          className="accent-[#C49A45]"
                          checked={current.includes(action)}
                          onChange={() => toggleAction(m.key, action)}
                        />
                        {action.replace(/_/g, ' ')}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-6 py-2.5 rounded shadow">
              {editingId ? 'UPDATE ROLE' : 'CREATE ROLE'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-[#FAF8F5] border border-[#E8DFD8] text-[#2B1810] font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded">
              CANCEL
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
